import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '@/store/store'
import { getCurrentUser, clearTokens } from '@/store/slices/authSlice'
import { fetchUnreadCount } from '@/store/slices/messagesSlice'
import { fetchUnreadCount as fetchNotificationUnreadCount } from '@/store/slices/notificationsSlice'
import { socketService } from '@/services/socketService'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

interface AuthContextType {
    isAuthenticated: boolean
    user: any
    isLoading: boolean
    error: string | null
    login: (credentials: any) => Promise<any>
    register: (userData: any) => Promise<any>
    logout: () => void
    checkAuth: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
    const context = useContext(AuthContext)
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const dispatch = useDispatch<AppDispatch>()
    const { user, token, isAuthenticated, isLoading, error } = useSelector(
        (state: RootState) => state.auth
    )

    const [hasCheckedAuth, setHasCheckedAuth] = useState(false)

    const checkAuth = async () => {
        if (token && !user && !hasCheckedAuth) {
            setHasCheckedAuth(true)
            try {
                // First, verify the user auth - this sets up proper token validation
                const userData = await dispatch(getCurrentUser()).unwrap()

                // Connect to socket first, then fetch counts
                socketService.connect(token, userData?._id)

                // Use a small delay to ensure socket connection and token headers are properly set
                setTimeout(async () => {
                    try {
                        // Fetch unread message count after successful authentication
                        await dispatch(fetchUnreadCount()).unwrap()
                        // Fetch unread notification count after successful authentication
                        await dispatch(fetchNotificationUnreadCount()).unwrap()
                    } catch (countError: any) {
                        console.warn('Failed to fetch unread counts:', countError)
                        // Don't clear tokens for count fetch failures
                    }
                }, 300) // Increased delay to allow socket connection
            } catch (error: any) {
                // Only clear tokens if it's definitely an auth error (401/403)
                // For network errors, keep tokens and let user try again
                if (error?.status === 401 || error?.status === 403) {
                    console.warn('Auth token is invalid or account status changed, clearing tokens:', error)
                    dispatch(clearTokens())

                    // If it's a forced logout due to account status, redirect to login with message
                    if (error?.forceLogout) {
                        // The snackbar message was already shown in the action
                        window.location.href = '/login'
                    }
                } else {
                    console.warn('Network error while checking auth, keeping tokens:', error)
                    // Reset flag so we can retry after a delay if needed
                    setTimeout(() => setHasCheckedAuth(false), 5000)
                }
            }
        }
    }

    useEffect(() => {
        // Only check auth if we have a token but no user data and not already loading
        if (token && !user && !isLoading) {
            checkAuth()
        }
        // Reset check flag when token changes or user is cleared
        if (!token || user) {
            setHasCheckedAuth(false)
        }
    }, [token, user, isLoading, dispatch])

    // Mock login/register/logout for context completeness
    // These will be handled by the redux actions in components
    const contextValue: AuthContextType = {
        isAuthenticated,
        user,
        isLoading,
        error,
        login: async (_credentials) => {
            // This will be handled by components using dispatch
            return Promise.resolve()
        },
        register: async (_userData) => {
            // This will be handled by components using dispatch
            return Promise.resolve()
        },
        logout: () => {
            // This will be handled by components using dispatch
        },
        checkAuth,
    }

    // Show loading spinner during initial auth check
    if (isLoading && token && !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <LoadingSpinner size="lg" text="Loading..." />
            </div>
        )
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}